import sortBy from 'lodash/sortBy';
import { useMemo } from 'react';

import { SimpleCardButton } from 'approot/shared/simple-card/simple-card';
import {
  ACTIVITY_BUILDER_A4_LONG_SIDE,
  SLIDE_HEIGHT_MINUS_HEADER_FOOTER,
  SectionOrSubSectionOverview,
} from 'approot/activity-builder/activity-builder.constants';
import { ActivityBuilderPagePreview } from 'approot/activity-builder/page/activity-builder-page-preview';
import {
  getInteractiveContentLabel,
  getSectionsAndSubSectionsOverview,
} from 'approot/activity-builder/activity-builder.utils';
import SimpleCardLabel from 'approot/shared/simple-card/simple-card-label';
import {
  ActivitiesContentStatus,
  ActivitiesContentType,
} from 'global/constants';
import { ActivityBuilderThemeContextProvider } from 'approot/activity-builder/theme/activity-builder-theme';
import { BoxList, Box } from 'approot/shared/design-system/box/box.style';
import { ActivityBuilderDrawerWrapper } from 'approot/activity-builder/activity-builder-drawer';
import { ActivityBuilderDrawerContent } from 'approot/activity-builder/activity-builder-drawer.style';
import { Heading16, Heading20 } from 'approot/shared/styles/typography.style';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import {
  BadgeSmallStyled,
  SubjectBadgeStyled,
} from 'approot/shared/badge/badge.style';
import { useYearTranslation } from 'approot/shared/logic/years';
import {
  useImageLoad,
  useResponsiveImage,
} from 'approot/shared/images/images.hooks';
import IconClose from 'approot/shared/icons/icon-close';
import { PublishedAssessmentActivities } from 'approot/activity-builder/graphql/get-published-assessment-activities.constants';
import { PublishedLessonActivities } from 'approot/activity-builder/graphql/get-published-lesson-activities.constants';
import * as Styled from './present-menu-activities.style';
import {
  pushActivityBuilderPresentActivitiesEvent,
  pushActivityBuilderPresentMenuEvent,
} from '../activity-builder-present-data-tracking.utils';
import {
  EVENT_ACTION_CLOSE,
  EVENT_TARGET_TYPE_ACTIVITIES,
} from 'approot/shared/data-tracker/data-events.constants';
import { InquisitiveLanguageCodes } from 'global/graphql/scalars.type';
import {
  DraftAssessmentActivities,
  DraftLessonActivities,
} from 'approot/activity-builder/activity-builder-draft.constants';
import { useGetActivityMetadata } from 'approot/activity-builder/logic/use-get-activity-details';

type Props = {
  contentId: number;
  contentType: ActivitiesContentType;
  classId: number | null;
  version?: string | null;
  language: InquisitiveLanguageCodes;
  isZoomToFit: boolean;
  currentSectionOverview: SectionOrSubSectionOverview | null;
  currentSectionOverviewIndex: number;
  status: ActivitiesContentStatus;
  sectionId: string;
  isPublic: boolean;
  data:
    | PublishedLessonActivities
    | PublishedAssessmentActivities
    | DraftLessonActivities
    | DraftAssessmentActivities;
  onActivityClick: (id: string) => void;
  onClose: () => void;
};

const PresentMenuActivities = ({
  contentId,
  contentType,
  classId,
  version,
  language,
  data,
  isZoomToFit,
  currentSectionOverview,
  currentSectionOverviewIndex,
  status,
  sectionId,
  isPublic,
  onActivityClick,
  onClose,
}: Props) => {
  const contentData = 'lesson' in data ? data.lesson : data.assessment;
  const overview = data?.sectionsOverview;

  const { formatYears } = useYearTranslation();
  const years = useMemo(
    () => contentData.years.map(y => y.name) || [],
    [contentData]
  );

  const sortedSectionsOverview = useMemo(
    () => sortBy(overview, 'sort'),
    [overview]
  );

  const sectionsAndSubSectionsOverview = useMemo(
    () => getSectionsAndSubSectionsOverview(sortedSectionsOverview),
    [sortedSectionsOverview]
  );

  const { getMetaData } = useGetActivityMetadata({
    sectionsOverview: sortedSectionsOverview,
    contentType,
  });

  const imageRatio =
    (SLIDE_HEIGHT_MINUS_HEADER_FOOTER / ACTIVITY_BUILDER_A4_LONG_SIDE) * 100;

  const imageUrl = useResponsiveImage(
    contentData.landscapeImage?.responsive || [] || [],
    320
  );
  const { imageHasLoaded } = useImageLoad(imageUrl || '');

  if (sectionsAndSubSectionsOverview.length === 0) return null;

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerContent $padding={0}>
        <Styled.PresentMenuImage
          $imageHasLoaded={imageHasLoaded}
          $imageUrl={imageUrl}
        />
        <Styled.PresentClose
          data-selector="close-activities"
          type="button"
          $isSmall
          aria-label="Close Activities"
          data-testid="close-activities"
          onClick={() => {
            pushActivityBuilderPresentMenuEvent({
              eventAction: EVENT_ACTION_CLOSE,
              eventTargetIdentifier: EVENT_TARGET_TYPE_ACTIVITIES,
              contentId,
              contentType,
              sectionId,
              currentSectionOverview,
              sectionsOverview: sectionsAndSubSectionsOverview,
              version: version || undefined,
              status,
              isZoomToFit,
              currentSectionOverviewIndex,
            });
            onClose();
          }}
        >
          <IconClose width={16} height={16} />
        </Styled.PresentClose>
        <Styled.PresentActivities>
          <Heading20 data-selector="activities-tab-name">
            {contentData.name}
          </Heading20>
          <Box $gap={8}>
            <BadgeSmallStyled data-selector="activities-tab-year">
              {formatYears(years)}
            </BadgeSmallStyled>
            <SubjectBadgeStyled
              data-selector="activities-tab-subject"
              $isSmall
              $colour={contentData.subjects[0].colour}
            >
              {contentData.subjects[0].label}
            </SubjectBadgeStyled>
          </Box>
        </Styled.PresentActivities>
        <Styled.PresentContent>
          <ActivityBuilderThemeContextProvider
            initialFontSize={data?.styles?.fontSize}
          >
            <Heading16 as="h2">
              {getInteractiveContentLabel(contentType)} Activities
            </Heading16>
            <BoxList>
              {sectionsAndSubSectionsOverview?.map((section, index) => {
                const { title, description, differentiation } =
                  getMetaData(section);

                return (
                  <Box
                    as="li"
                    key={section.id}
                    data-selector={`activity-${index}`}
                  >
                    <SimpleCardButton
                      title={
                        <SimpleCardLabel
                          title={title}
                          description={description}
                          differentiation={differentiation}
                        />
                      }
                      ariaTitle={`${title}${
                        description ? ` - ${description}` : ''
                      }`}
                      data-testid={`activity-${index}`}
                      imageRatio={imageRatio}
                      bodyText={
                        <ActivityBuilderPagePreview
                          id={section.id}
                          contentId={contentId}
                          classId={classId}
                          parentElement={null}
                          contentType={contentType}
                          version={version}
                          language={language}
                          isPublic={isPublic}
                        />
                      }
                      onClick={() => {
                        pushActivityBuilderPresentActivitiesEvent({
                          contentId,
                          contentType,
                          sectionId,
                          clickedSectionIndex: index,
                          currentSectionOverview,
                          sectionsOverview: sectionsAndSubSectionsOverview,
                          version: version || undefined,
                          status,
                          isZoomToFit,
                          currentSectionOverviewIndex,
                        });
                        onActivityClick(section.id);
                      }}
                    />
                    {index !== sectionsAndSubSectionsOverview.length - 1 && (
                      <HrStyled $marginBottom={24} />
                    )}
                  </Box>
                );
              })}
            </BoxList>
          </ActivityBuilderThemeContextProvider>
        </Styled.PresentContent>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default PresentMenuActivities;
